import { useEffect, useMemo, useRef, useState } from 'react';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Switch from '@mui/material/Switch';

import { configureProject } from 'src/api/projects';
import { Vertical } from 'src/components/Alignments';
import FormBox from 'src/components/FormBox';
import CommonDialog, {
  CommonDialogProps,
} from 'src/components/Modal/CommonDialog';
import { INITIAL_ASSIGN_RULE } from 'src/configs/claims';
import CXRClaim from 'src/configs/claims/CXR';
import DBTClaim from 'src/configs/claims/DBT';
import MMGClaim from 'src/configs/claims/MMG';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import AssetValidations from 'src/pages/NewProject/AssetValidations';
import Assets from 'src/pages/NewProject/Assets';
import CorroborationForm from 'src/pages/NewProject/BasicInformation/CorroborationForm';
import InvisibleComponents from 'src/pages/NewProject/BasicInformation/InvisibleComponents';
import Controls from 'src/pages/NewProject/Controls';
import FindingFields from 'src/pages/NewProject/FindingFields';
import FormHeader from 'src/pages/NewProject/FormHeader';
import Modes from 'src/pages/NewProject/Modes';
import Overrides from 'src/pages/NewProject/Overrides';
import Overview from 'src/pages/NewProject/Overview';
import { newProjectState } from 'src/states/newProject';
import { projectState, useRefreshProject } from 'src/states/project';
import { Modality } from 'src/types/api/data/image';
import { ClaimCreateSchema } from 'src/types/api/data/project';

type Props = CommonDialogProps & {
  projectId: string;
  modalityLabel: Modality;
  onUpdated?: () => void;
};

const STEPS = [
  'Basic',
  'Modes',
  'Controls',
  'Assets',
  'Asset Validations',
  'Finding Fields',
  'Overrides',
  'Overview',
] as const;

const ConfigureBasicInformation = (): JSX.Element => {
  const [claim, setClaim] = useRecoilState(newProjectState.claim);

  const isCPC = claim.type === 'CurrentPriorComparison';

  const handleChangeProjectType = () => {
    setClaim(prev => ({
      ...prev,
      type: prev.type === 'Standard' ? 'CurrentPriorComparison' : 'Standard',
    }));
  };

  return (
    <Vertical style={{ gap: '1rem' }}>
      <FormBox>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={isCPC}
                onChange={handleChangeProjectType}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
            label="Is this CPC (CurrentPriorComparison) project?"
          />
        </FormGroup>
      </FormBox>
      <InvisibleComponents />
      <CorroborationForm />
    </Vertical>
  );
};

export default function ConfigureProjectDialog({
  open,
  onClose,
  onUpdated,
  projectId,
  modalityLabel,
  fullWidth,
  maxWidth,
}: Props): JSX.Element {
  const setModalityLabel = useSetRecoilState(newProjectState.modalityLabel);
  const [claim, setClaim] = useRecoilState(newProjectState.claim);
  const setName = useSetRecoilState(newProjectState.name);
  const project = useRecoilValue(
    projectState.project({ projectId, modality: modalityLabel })
  );
  const originalClaimRef = useRef<ClaimCreateSchema | null>(null);

  const { openAlertSnackbar } = useAlertSnackbar();
  const refreshProject = useRefreshProject();

  const [currentStep, setCurrentStep] = useState<(typeof STEPS)[number]>(
    STEPS[0]
  );
  const [isSaving] = useState(false);

  // initialize modality into recoil state for sub-forms that rely on it
  useEffect(() => {
    if (open) {
      setModalityLabel(modalityLabel);
    }
  }, [open, modalityLabel, setModalityLabel]);

  useEffect(() => {
    if (!open) {
      // reset sentinel so next open initializes again
      originalClaimRef.current = null;
      return;
    }

    // Initialize claim and name once when opening
    if (open && project?.claim && !originalClaimRef.current) {
      const normalized = normalizeClaimForEditor(
        project.claim as ClaimCreateSchema,
        modalityLabel
      );
      setClaim(normalized);
      setName(project.name || '');
      originalClaimRef.current = JSON.parse(JSON.stringify(normalized));
    }
  }, [open, project, setClaim, setName, modalityLabel]);

  const handleReset = () => {
    if (originalClaimRef.current) {
      setClaim(originalClaimRef.current);
    }
  };

  const isValidForConfigure = useMemo(() => {
    // For configure, ignore name validation; ensure assets/validations are valid
    // We will reuse the same validation components visually, but here only block save if JSON overrides invalid
    return true; // Sub-forms show validation warnings; API will also validate claim
  }, []);

  const handleSave = async () => {
    // Close modal immediately upon clicking save (optimistic close)
    onClose?.();
    try {
      await configureProject(projectId, modalityLabel, claim);
      openAlertSnackbar({
        severity: 'success',
        description: 'Project configured successfully.',
      });
      onUpdated?.();
      refreshProject();
    } catch {
      openAlertSnackbar({
        severity: 'error',
        description: 'Failed to configure project.',
      });
    }
  };

  return (
    <CommonDialog
      onClose={onClose}
      open={open}
      title="Configure Project"
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      scroll="paper"
      PaperProps={{
        sx: {
          height: '80vh',
        },
      }}
    >
      <DialogContent>
        <FormHeader
          items={STEPS}
          currentItem={currentStep}
          setCurrentItem={setCurrentStep}
          resetButtonElement={
            <Button
              color="secondary"
              size="large"
              variant="contained"
              onClick={handleReset}
            >
              Reset
            </Button>
          }
          submitButtonElement={
            <Button
              color="primary"
              size="large"
              variant="contained"
              onClick={handleSave}
              disabled={!isValidForConfigure || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          }
        />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          {currentStep === 'Basic' && <ConfigureBasicInformation />}
          {currentStep === 'Modes' && <Modes />}
          {currentStep === 'Controls' && <Controls />}
          {currentStep === 'Assets' && (
            <DndProvider backend={HTML5Backend}>
              <Assets />
            </DndProvider>
          )}
          {currentStep === 'Asset Validations' && (
            <DndProvider backend={HTML5Backend}>
              <AssetValidations />
            </DndProvider>
          )}
          {currentStep === 'Finding Fields' && <FindingFields />}
          {currentStep === 'Overrides' && <Overrides />}
          {currentStep === 'Overview' && <Overview />}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleSave}
          disabled={!isValidForConfigure || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </CommonDialog>
  );
}

function normalizeClaimForEditor(
  serverClaim: ClaimCreateSchema,
  modalityLabel: Modality
): ClaimCreateSchema {
  const assignRule = {
    ...INITIAL_ASSIGN_RULE,
    ...serverClaim.assignRule,
    corroboration: {
      ...INITIAL_ASSIGN_RULE.corroboration,
      ...(serverClaim.assignRule?.corroboration || {}),
    },
  };

  type AssetWithMaybeSelected = ClaimCreateSchema['assets'][number] & {
    selectedValues?: (string | boolean)[];
  };

  const assets = Array.isArray(serverClaim.assets)
    ? (serverClaim.assets as AssetWithMaybeSelected[]).map(asset => ({
        selectedValues: Array.isArray(asset.selectedValues)
          ? asset.selectedValues
          : [],
        ...asset,
      }))
    : [];

  // Get default viewer based on modality
  const getDefaultViewer = (): ClaimCreateSchema['viewer'] => {
    switch (modalityLabel) {
      case Modality.CXR:
        return CXRClaim.viewer;
      case Modality.MMG:
        return MMGClaim.viewer;
      case Modality.DBT:
        return DBTClaim.viewer;
      default:
        return { type: 'xray', images: [] };
    }
  };

  return {
    version: serverClaim.version || '1.1',
    controls: Array.isArray(serverClaim.controls) ? serverClaim.controls : [],
    findingFields: serverClaim.findingFields || [],
    viewer: serverClaim.viewer || getDefaultViewer(),
    modes: Array.isArray(serverClaim.modes) ? serverClaim.modes : [],
    assetValidations: serverClaim.assetValidations || [],
    overrides: serverClaim.overrides || [],
    assignRule,
    invisibleComponents: serverClaim.invisibleComponents || [],
    type:
      (serverClaim as unknown as { type?: ClaimCreateSchema['type'] }).type ||
      'Standard',
    assets,
  } as ClaimCreateSchema;
}
